import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Select, FormControl, FormLabel,
  VStack, HStack, Text, Badge, Progress, Box,
  Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, useColorModeValue,
} from '@chakra-ui/react';
import { updateAdminUser } from 'services/wpUsers';

const ROLES = [
  { value: 'customer',   label: 'Activo',   color: 'green'  },
  { value: 'becado',     label: 'Becado',   color: 'purple' },
  { value: 'cobranza',   label: 'Cobranza', color: 'orange' },
  { value: 'subscriber', label: 'Archivado', color: 'gray'  },
];

const BatchRoleModal = ({ isOpen, onClose, customers = [], onDone }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // { updated, errors }
  const [progress, setProgress] = useState(0);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedText   = useColorModeValue('gray.500', 'gray.400');

  const reset = () => {
    setSelectedRole('');
    setResults(null);
    setProgress(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleApply = async () => {
    if (!selectedRole || customers.length === 0) return;
    setLoading(true);
    setResults(null);
    setProgress(0);

    const updated = [];
    const errors  = [];

    for (let i = 0; i < customers.length; i++) {
      const c = customers[i];
      try {
        await updateAdminUser(c.id, { role: selectedRole });
        updated.push({ id: c.id, name: `${c.first_name} ${c.last_name}`, username: c.username, prevRole: c.role });
      } catch (err) {
        errors.push({ id: c.id, name: `${c.first_name} ${c.last_name}`, error: err.message || 'Error desconocido' });
      }
      setProgress(Math.round(((i + 1) / customers.length) * 100));
    }

    setResults({ updated, errors });
    setLoading(false);
  };

  const roleName = ROLES.find(r => r.value === selectedRole)?.label || '';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cambiar Rol de Usuarios</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">

            {!results && (
              <>
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Se cambiará el rol de <strong>&nbsp;{customers.length}&nbsp;</strong> usuario{customers.length !== 1 ? 's' : ''} seleccionado{customers.length !== 1 ? 's' : ''}.
                </Alert>

                <FormControl>
                  <FormLabel>Nuevo rol</FormLabel>
                  <Select
                    placeholder="Selecciona un rol"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    {ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </Select>
                </FormControl>

                {selectedRole && (
                  <Box p={3} bg={useColorModeValue('yellow.50','yellow.900')} borderRadius="md" border="1px solid" borderColor={useColorModeValue('yellow.200','yellow.600')}>
                    <Text fontSize="sm" color={useColorModeValue('yellow.700','yellow.200')}>
                      ⚠️ Esto cambiará el acceso de los usuarios seleccionados al rol <strong>{roleName}</strong>. Esta acción es reversible.
                    </Text>
                  </Box>
                )}

                {loading && (
                  <Progress value={progress} colorScheme="purple" borderRadius="full" size="sm" hasStripe isAnimated />
                )}
              </>
            )}

            {results && (
              <VStack spacing={3} align="stretch">
                <HStack spacing={4}>
                  <Badge colorScheme="green" fontSize="sm" px={2} py={1}>✓ {results.updated.length} actualizados</Badge>
                  {results.errors.length > 0 && (
                    <Badge colorScheme="red" fontSize="sm" px={2} py={1}>✗ {results.errors.length} errores</Badge>
                  )}
                </HStack>

                {results.updated.length > 0 && (
                  <Box overflowX="auto" border="1px solid" borderColor={borderColor} borderRadius="md">
                    <Table size="sm">
                      <Thead><Tr><Th>Nombre</Th><Th>RUT</Th><Th>Rol anterior</Th><Th>Nuevo rol</Th></Tr></Thead>
                      <Tbody>
                        {results.updated.map((u, i) => (
                          <Tr key={i}>
                            <Td fontSize="xs">{u.name}</Td>
                            <Td fontSize="xs" fontFamily="mono">{u.username}</Td>
                            <Td><Badge colorScheme="gray" fontSize="xs">{u.prevRole}</Badge></Td>
                            <Td><Badge colorScheme={ROLES.find(r => r.value === selectedRole)?.color} fontSize="xs">{roleName}</Badge></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {results.errors.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="red.400" mb={2}>Errores</Text>
                    {results.errors.map((e, i) => (
                      <Text key={i} fontSize="xs" color="red.400">{e.name}: {e.error}</Text>
                    ))}
                  </Box>
                )}
              </VStack>
            )}

          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            {!results ? (
              <>
                <Button
                  colorScheme="purple"
                  onClick={handleApply}
                  isLoading={loading}
                  loadingText="Aplicando..."
                  isDisabled={!selectedRole || customers.length === 0}
                >
                  Aplicar a {customers.length} usuario{customers.length !== 1 ? 's' : ''}
                </Button>
                <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
              </>
            ) : (
              <>
                <Button colorScheme="teal" onClick={() => { onDone && onDone(); reset(); }}>
                  Listo
                </Button>
                <Button variant="ghost" onClick={reset}>Cambiar más</Button>
              </>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BatchRoleModal;
